import { detectPlatformAdapter } from '../platform'

const adapter = detectPlatformAdapter()

export interface SharePayload {
  url: string
  text: string
  /** Optional files (PDF, image…) to attach via the system share sheet. */
  files?: File[]
}

/**
 * Hard timeout for navigator.share. TG WebView's implementation
 * sometimes never resolves (button stays in loading state forever), so
 * we race against this and fall through to the bridge / clipboard
 * paths instead of hanging the UI.
 */
const NAVIGATOR_SHARE_TIMEOUT_MS = 4000

/**
 * Share to whatever's available, in this priority:
 *  1. Inside TG WebView WITHOUT files — go straight to the TG bridge
 *     (t.me/share/url). It's reliable and predictable; navigator.share
 *     inside TG sometimes hangs forever which we used to inherit.
 *  2. Web Share API (`navigator.share`) — gives the user the OS share
 *     sheet. Used outside TG, and for ANY share that carries files
 *     (PDF natal chart, etc.) since the bridge can't attach those.
 *  3. Platform adapter share — link-only TG/MAX bridge openers.
 *  4. Clipboard — last resort, copies text + url.
 */
export function useShare() {
  return async (payload: SharePayload) => {
    const hasFiles = !!(payload.files && payload.files.length > 0)
    const inTg = adapter.platform === 'telegram'

    // Inside TG mini-app, link-only share goes straight to the bridge.
    // Skips the navigator.share hang trap entirely.
    if (inTg && !hasFiles) {
      try {
        await adapter.share({ url: payload.url, text: payload.text })
        return
      } catch {
        /* fall through to navigator.share / clipboard */
      }
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      const data: ShareData = {}
      if (payload.text) {
        data.title = payload.text
        data.text = payload.text
      }
      if (payload.url) data.url = payload.url
      if (hasFiles) {
        const canShareFiles = navigator.canShare?.({ files: payload.files })
        if (canShareFiles) data.files = payload.files
      }
      try {
        // Race against a timeout — TG WebView's navigator.share can hang.
        await Promise.race([
          navigator.share(data),
          new Promise<never>((_, rej) =>
            setTimeout(() => rej(new Error('share-timeout')), NAVIGATOR_SHARE_TIMEOUT_MS),
          ),
        ])
        return
      } catch (e) {
        const name = (e as Error).name
        const msg = (e as Error).message
        // User cancelled — silent. Timeout / other errors fall through.
        if (name === 'AbortError') return
        if (msg === 'share-timeout') {
          // eslint-disable-next-line no-console
          console.warn('navigator.share timed out, falling back to bridge / clipboard')
        }
      }
    }

    try {
      await adapter.share({ url: payload.url, text: payload.text })
      return
    } catch {
      /* fall through */
    }

    await navigator.clipboard
      .writeText(`${payload.text}\n${payload.url}`)
      .catch(() => {})
  }
}
